import { create } from 'zustand';
import { documentCaptureService } from '../services/documentCaptureService';
import type {
  DocStreamEvent,
  DocStreamPhase,
  DocumentListItem,
  DocumentRegistryQuery,
  IntakeToday,
  ReviewQueueItem,
} from '../types/documentCapture.types';
import { tabForItem, type ReviewTab } from '../config/reviewTabs';
import { DEFAULT_REGISTRY_PAGE_SIZE } from '../config/verification';
// 86d3y0cku — imported rather than re-spelled inline: the union gained a terminal 'offline' member
// when the reconnect budget was capped, and three copies of it had already started to drift.
import type { DocStreamConnectionState } from '../lib/documentCaptureStream';
// Type-only, so this module does not drag the watcher's File System Access code into every bundle
// that imports the store — the Sidebar's review badge imports it on every route.
import type { WatcherProgress } from '../lib/scanWatcher';
import {
  addLocalRow,
  reduceStreamEvent,
  resolveLocalRow,
  retireUnwatched,
  settleResolvedRow,
  STREAM_CAP,
  sweepRetired,
  type JourneyRow,
} from '../lib/streamReducers';

/**
 * Document Capture state (86d3rrfbf).
 *
 * ⚠️ SELECTOR DISCIPLINE (React Compiler — see CLAUDE.md 86d3d1z40): every field here is a plain
 * VALUE. Do not add getters that read `get()` internally and then call them in render — the
 * compiler memoizes on the stable function reference and serves stale UI. Components subscribe to
 * values (`useDocumentCaptureStore((s) => s.needsReviewCount)`) and derive from those. Actions may
 * use `get()` freely; only render must not.
 *
 * The live-journey reconciliation logic lives as pure functions in ../lib/streamReducers (unit-tested
 * in streamReducers.test.ts); the actions here are thin wrappers that call them + do side effects.
 */

// Re-exported because LiveStreamPanel imports `JourneyRow` from here — the definition itself lives in
// streamReducers. `SessionTally` used to ride along on this line and no longer does: when 86d3yek7d
// took the day's numbers off the feed, its last consumer went with them, and a re-export nobody
// imports is a signpost to a road that was removed. The type is still exported from streamReducers,
// where `tallyFromRows` returns it.
export type { JourneyRow };

export type StationState = 'watching' | 'nofolder' | 'resume' | 'unsupported' | 'lost';

interface DocumentCaptureState {
  // ---- live journey ----
  stream: JourneyRow[];
  streamConnection: DocStreamConnectionState;

  // ---- station ----
  stationState: StationState;
  stationFolderName: string | null;
  /**
   * How much the local watcher is holding right now — waiting, uploading, still settling. Null when
   * no watcher is reporting (no upload right, unsupported browser, or not watching a folder).
   *
   * This exists for backfills: users paste thousands of PDFs into the watched folder, and without a
   * number the only thing the page can say for the next hour is "Watching".
   */
  stationQueue: WatcherProgress | null;

  // ---- today (86d3yek7d) ----
  //
  // The store used to carry `filesToday` here too — a counter this tab bumped on every upload, which
  // the card rendered as "scanned". It reset on reload, never rolled over at midnight, and drifted
  // from the ScanWatcher's own private copy (that one still exists and still feeds the station
  // heartbeat; it is the only client-side count left, and nothing on this page displays it).
  /** Server-counted intake for the current Philippine day. Null until the first fetch lands. */
  intakeToday: IntakeToday | null;
  /** True only for the FIRST load — background refreshes must never blank numbers already on screen. */
  intakeTodayLoading: boolean;
  intakeTodayError: string | null;

  // ---- registry ----
  registryQuery: DocumentRegistryQuery;
  documents: DocumentListItem[];
  registryTotal: number;
  registryLoading: boolean;
  registryError: string | null;
  /**
   * 86d3z56ma A3 — is the "All documents" panel the one on screen? Published by the page, because the
   * panel itself is LOCAL state there and must stay that way: `?view=` seeds it once in a `useState`
   * initializer and the URL is write-only afterwards, so lifting the panel into this module-scoped
   * store would start a `?view=registry` deep link on the stream, and leak the tab across routes.
   *
   * This is the flag, not the state: the page owns `panel` and mirrors it here in an effect that
   * clears on unmount.
   */
  registryVisible: boolean;

  // ---- review ----
  // The full open queue, fetched once and grouped by REASON (not the server's mis-aligned Kind).
  reviewItems: ReviewQueueItem[];
  reviewCounts: { scans: number; netsuite: number; replacements: number; total: number };
  needsReviewCount: number;
  reviewLoading: boolean;
  reviewError: string | null;

  // ---- review overlay ----
  reviewOverlayOpen: boolean;
  reviewActiveTab: ReviewTab;
  /** The item currently selected in the overlay (drives the side-by-side + action form). */
  selectedReviewItemId: number | null;

  // ---- actions ----
  applyStreamEvent: (event: DocStreamEvent) => void;
  hydrateStream: () => Promise<void>;
  /**
   * 86d3yeh5j — step finished rows aside once they have had their moment. Driven by an interval in a
   * PAGE effect (with cleanup), never a module-scope timer: this store module loads app-wide via the
   * Sidebar's review badge, so a timer here would tick in every session on every route forever.
   */
  sweepStream: () => void;
  setStreamConnection: (state: DocStreamConnectionState) => void;
  addLocalJourneyRow: (key: string, fileName: string, clientUploadId?: string) => void;
  resolveLocalJourneyRow: (
    key: string,
    phase: DocStreamPhase,
    detail?: Partial<Pick<JourneyRow, 'docNumber' | 'prefix' | 'fileId' | 'duplicateStatus'>>
  ) => void;
  /**
   * 86d3x48q7 (T13) — a reviewer just identified this scan by hand. Walks its stream row through the
   * linking step instead of letting it jump to `linked`, so the feed shows the round trip. A no-op
   * when the row isn't on screen (someone else's upload, or an older session).
   */
  settleReviewedRow: (match: { fileId: number | null; docNumber: string | null }) => void;

  setStationState: (state: StationState, folderName?: string | null) => void;
  /** Fed by the watcher's `onProgress`, which only fires when the numbers actually change. */
  setStationQueue: (progress: WatcherProgress) => void;

  /**
   * 86d3yek7d — read today's numbers from the server. `stationId` is remembered, so the SSE-driven
   * refresh below keeps asking about the same station without the caller re-supplying it.
   */
  fetchIntakeToday: (stationId?: string) => Promise<void>;
  /**
   * Refetch when the Philippine day has rolled over under a tab nobody reloaded. Cheap enough to call
   * from the existing sweep tick: it compares two date strings and returns.
   */
  refreshIntakeIfDayChanged: () => void;

  setRegistryQuery: (query: DocumentRegistryQuery) => void;
  /** `silent` skips the blocking loading flag — for refreshes the user didn't ask for. */
  fetchDocuments: (silent?: boolean) => Promise<void>;
  /** 86d3z56ma A3 — mirror of the page's local `panel`. Never fetches; see the field's note. */
  setRegistryVisible: (visible: boolean) => void;
  /**
   * 86d3z56ma A3 — the browser tab just came back to the foreground. Runs the ONE registry refetch a
   * gated stream event left owing, and nothing at all if none is owed. Called from a page listener,
   * never from a module-scope one: this store loads app-wide via the Sidebar's review badge.
   */
  flushRegistryRefetch: () => void;

  /** One fetch → the tab items, per-tab counts, and the badge total all derive from it. */
  fetchReviewQueue: () => Promise<void>;
  openReview: (tab?: ReviewTab, reviewItemId?: number) => void;
  closeReview: () => void;
  setReviewTab: (tab: ReviewTab) => void;
  selectReviewItem: (reviewItemId: number | null) => void;
}

/**
 * Group the flat queue into the three display tabs by reason, deriving counts.
 *
 * ⚠️ `total` is the SERVER's count, not `items.length`. The fetch is capped at 100 rows, so deriving
 * the total from what came back silently pins the badge at "100" once the queue is fuller than that
 * — and reports it as though it were the real number. `AiSuggestion` is expected to be the largest
 * group once the second reader is live, so this stops being hypothetical on day one. The per-tab
 * counts are still derived, because the server does not group by the FE's reason taxonomy; they can
 * therefore under-report past 100 while `total` stays honest, which is the right way round: the
 * badge is the number people act on.
 */
function groupReview(items: ReviewQueueItem[], totalCount: number) {
  const byTab: Record<ReviewTab, ReviewQueueItem[]> = { scans: [], netsuite: [], replacements: [] };
  for (const item of items) byTab[tabForItem(item)].push(item);
  return {
    byTab,
    counts: {
      scans: byTab.scans.length,
      netsuite: byTab.netsuite.length,
      replacements: byTab.replacements.length,
      total: totalCount,
    },
  };
}

/** Debounce for the SSE-driven review-queue refetch (86d3uf18j M3) — collapses bursts + hydrate replay. */
let reviewRefetchTimer: ReturnType<typeof setTimeout> | null = null;

/** Same, for the registry — see the note in applyStreamEvent. */
let registryRefetchTimer: ReturnType<typeof setTimeout> | null = null;

/** Same again for today's numbers (86d3yek7d) — but debounced WITH A CEILING; see scheduleIntakeRefetch. */
let intakeRefetchTimer: ReturnType<typeof setTimeout> | null = null;

/** Epoch ms by which the burst in progress must have refetched. 0 = no burst in progress. */
let intakeRefetchDeadline = 0;

/** Quiet-period collapse: the 3+ events one file emits become one fetch. */
const INTAKE_DEBOUNCE_MS = 400;

/** The ceiling that guarantees progress when the quiet period never comes. */
const INTAKE_MAX_WAIT_MS = 3_000;

/**
 * Debounce-with-max-wait for the "Today" refetch.
 *
 * A pure TRAILING debounce is a trap for a live counter, and it was the shape here: every event
 * cleared the pending timer, so a burst that never pauses for 400 ms never fetched AT ALL. That is
 * not a corner case for this card — it is the exact load it exists for. `INTAKE_AFFECTING_PHASES`
 * matches three or more of the events a single file emits (reading → captured → linked), so an
 * archive catch-up of a few hundred scans in an afternoon comfortably out-paces one event per 400 ms.
 * The card headed "Today" would then sit on whatever it read at mount until the operator stopped
 * feeding the scanner — frozen precisely while the day's work was happening, and frozen in a way that
 * is indistinguishable from being correct.
 *
 * So: keep the 400 ms collapse for quiet periods, but never let more than INTAKE_MAX_WAIT_MS pass
 * between fetches while events keep arriving. (A leading+trailing throttle would be equivalent; this
 * shape was chosen because it degrades to the previous behaviour exactly when the stream is quiet.)
 * Simply shortening the debounce was rejected: it would hammer the aggregate query on every burst and
 * STILL never guarantee a fetch — more load and the same bug.
 *
 * ⚠️ The review and registry debounces above are left as pure debounces DELIBERATELY, not by
 * oversight. A queue list and an archive table are read when you look at them, and a burst is exactly
 * when they are churning too fast to read: arriving a beat late is free, and refetching either
 * mid-burst costs a 100-row query. A counter is the opposite — it is read at a glance, WHILE the
 * burst is happening, and a number that has stopped moving reads as a number that is right.
 *
 * `fetchIntakeToday` carries its own sequence guard, so a fire that overlaps an in-flight fetch
 * resolves to the newer answer rather than racing.
 */
function scheduleIntakeRefetch(run: () => void) {
  const now = Date.now();
  // The first event of a burst starts the clock; every later one inherits the same deadline, which
  // is what stops the ceiling from being pushed back forever alongside the debounce.
  if (intakeRefetchDeadline === 0) intakeRefetchDeadline = now + INTAKE_MAX_WAIT_MS;
  const delay = Math.max(0, Math.min(INTAKE_DEBOUNCE_MS, intakeRefetchDeadline - now));
  if (intakeRefetchTimer) clearTimeout(intakeRefetchTimer);
  intakeRefetchTimer = setTimeout(() => {
    intakeRefetchTimer = null;
    intakeRefetchDeadline = 0; // the next event opens a fresh window
    run();
  }, delay);
}

// ---------------------------------------------------------------- hydrate / live ordering
//
// THE RACE THIS CLOSES. `DocumentCapturePage` fires `hydrateStream()` un-awaited and attaches the
// live SSE subscription synchronously — which is right, and must stay that way: delaying the
// subscription until the replay lands would trade a REORDERING for a GAP, and a missed event is
// strictly worse than a late one. But it means a replayed snapshot (fetched before a live event
// happened) could be applied AFTER that live event, walking a row backwards. `held`/`retrying` are
// guarded in the reducer (STALLED_STEP_PHASES), but `reading`/`verifying` cannot be: the W9 replace
// flow publishes exactly those backwards on purpose, so from inside the reducer a replay is
// indistinguishable from a live replace. The row stranded on `verifying` — not retirable, so it never
// swept away — and the operator read "checking it independently" on a scan that was captured and filed.
//
// The fix belongs HERE rather than in the page, because the store owns `hydrateStream` and is
// therefore the only thing that knows a replay is in flight. While one is, live events QUEUE; when it
// lands, the replay is applied first (oldest-first, as before) and the queue is drained after it, in
// arrival order. The replay can then never sit on top of newer live state.

/** Monotonic id for the in-flight replay — same idiom as `registrySeq`/`intakeSeq` below. */
let hydrateSeq = 0;

/**
 * Which hydrate the queue belongs to. 0 means the gate is OPEN: events land immediately, which is the
 * state for all but a few hundred milliseconds per page mount.
 */
let liveBufferSeq = 0;

/** Live events received while a replay is in flight, in arrival order. */
let liveBuffer: DocStreamEvent[] = [];

/**
 * The bound. Four times the replay window, which is the number that makes discarding the replay on
 * overflow defensible: the replay describes at most STREAM_CAP events, so by the time this many
 * NEWER events have arrived, essentially every row it could speak about has been overwritten, and
 * applying it would be the very bug this seam exists to prevent.
 *
 * Overflow behaviour (see `applyStreamEvent`): the gate OPENS — the queue is drained immediately,
 * later events land directly, and the in-flight replay is discarded when it arrives. Deliberately
 * not a ring buffer: dropping the oldest live events would keep the feed frozen for as long as the
 * hydrate hung, which is the failure this whole change is about, in a new costume.
 *
 * ⚠️ Time is bounded too, and by something that already exists rather than a timer of our own:
 * `apiClient` carries a 30 s timeout, so `getRecentEvents` always settles and `hydrateStream`'s
 * try/catch always reaches the drain. Worst case on a QUIET station whose hydrate hangs is therefore
 * a feed that lags by up to that timeout — bad, but bounded, self-healing, and far rarer than the
 * race being fixed. A busy station never gets there: it hits the bound first and opens the gate.
 */
const LIVE_BUFFER_MAX = STREAM_CAP * 4;

/** Monotonic request id so a slow registry response can never overwrite a newer one. */
let registrySeq = 0;

// ---------------------------------------------------------------- registry refetch gate (86d3z56ma A3)
//
// ⚠️ FRAMING. This is LOAD REDUCTION, not the cure for the production timeout that opened the ticket:
// the person who hit it was ON "All documents" with an identification filter applied, which is exactly
// where this gate keeps today's behaviour. The cure is the missing index on ScannedDocumentFiles.
// What this stops is the same multi-second query being run behind a stream event by every page that
// is not looking at the archive — including parked browser tabs, which the shared bus keeps feeding
// (docCaptureBus only consults visibilityState to decide whether to RETRY a dead stream), so a DOP tab
// behind three others costs exactly what a watched one does and needs nobody sitting there.

/** True while a `/documents` request is in flight, so a background refetch can defer behind it. */
let registryInFlight = false;

/**
 * A background refetch that was suppressed — gated, or deferred behind an in-flight request — and is
 * still owed. Exactly one is ever owed: the archive has one current state, so two suppressed events
 * do not need two fetches.
 */
let registryRefetchOwed = false;

/** Aborts the request a newer one supersedes. See `fetchDocuments`. */
let registryAbort: AbortController | null = null;

/**
 * May a BACKGROUND registry refetch run right now?
 *
 * Both halves are required, and the browser half is the one that was decided rather than assumed
 * (owner, 2026-08-07): a hidden tab still receives every event, so gating only on the panel would
 * leave the likeliest source of the concurrent production queries untouched.
 *
 * `typeof document` guarded because this module is unit-tested without a DOM.
 */
function backgroundRegistryFetchAllowed(registryVisible: boolean): boolean {
  if (!registryVisible) return false;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return false;
  return true;
}

/** Same guard for the intake stats: a debounced refetch races the mount fetch. */
let intakeSeq = 0;

/**
 * The station this browser uploads as, remembered from the first `fetchIntakeToday` so the
 * SSE-driven refresh keeps asking about the same one. Module scope, like the timers above.
 */
let intakeStationId: string | undefined;

/**
 * Stream phases that mean a REGISTRY row was created or changed, so the archive list is now stale.
 *
 * ⚠️ Kept deliberately tight. `waiting` and `review` are NOT here even though 86d3z2nyz's approve
 * path can now derive either of them, and it is worth writing down why, because the omission looks
 * like the bug it is not. Both phases are published overwhelmingly by scans that have NO document
 * behind them at all — a held file, an unidentified scan awaiting a person — and during a backfill
 * that is most of the traffic. Admitting them here would run the archive query continuously through
 * every burst to catch a case that is rare by construction.
 *
 * Accepted residual: approving a replacement on a document that is NOT yet linked (Pending,
 * AwaitingNs, Conflict, closed period) changes which version the archive should show, and the
 * archive will keep showing the old one until the next qualifying event or a manual refresh. The
 * common path — a replacement on an already-linked document, which is the whole reason replacements
 * exist — derives `updated` and is covered.
 */
const REGISTRY_AFFECTING_PHASES: ReadonlySet<DocStreamPhase> = new Set<DocStreamPhase>([
  'captured', // a new document row exists
  'linked', // its link status changed
  'updated', // T7 replaced its current file, or a replacement was approved on a linked document
]);

/**
 * 86d3yek7d — stream phases that move one of today's numbers, so the card is now stale.
 *
 * An explicit ALLOW-LIST rather than "refetch on every event": a busy station emits several events
 * per file (verifying / retrying / held / waiting), none of which change a count, and refetching
 * behind each one would keep a query running continuously through the debounce for no new number.
 *   reading  → a file row now exists (scanned)
 *   captured → it is attached (captured, and it leaves failed)
 *   linked   → its document is linked
 *   failed   → a terminal decode verdict
 *   updated  → T7 wrote a NEW file row for an existing document (scanned)
 */
const INTAKE_AFFECTING_PHASES: ReadonlySet<DocStreamPhase> = new Set<DocStreamPhase>([
  'reading',
  'captured',
  'linked',
  'failed',
  'updated',
]);

/**
 * Today's date in Manila as `yyyy-MM-dd` — the same string the server stamps on the payload.
 * `en-CA` because it formats as ISO; the alternative is hand-rolling an offset, and the whole point
 * of asking the server for `day` is that nobody has to.
 */
function philippineToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Manila' }).format(new Date());
}

export const useDocumentCaptureStore = create<DocumentCaptureState>()((set, get) => ({
  stream: [],
  streamConnection: 'connecting',

  stationState: 'nofolder',
  stationFolderName: null,
  stationQueue: null,

  intakeToday: null,
  intakeTodayLoading: false,
  intakeTodayError: null,

  registryQuery: { page: 1, pageSize: DEFAULT_REGISTRY_PAGE_SIZE },
  documents: [],
  registryTotal: 0,
  registryLoading: false,
  registryError: null,
  // False until the page says otherwise: the capture page opens on the live stream, and the Sidebar's
  // review badge imports this store on every route where no registry panel exists at all.
  registryVisible: false,

  reviewItems: [],
  reviewCounts: { scans: 0, netsuite: 0, replacements: 0, total: 0 },
  needsReviewCount: 0,
  reviewLoading: false,
  reviewError: null,

  reviewOverlayOpen: false,
  reviewActiveTab: 'scans',
  selectedReviewItemId: null,

  // ---------------------------------------------------------------- live journey

  /**
   * Morph-in-place. Matches an existing row by file id, falling back to document number for
   * document-level events (the link lane sends those with a null file id). An unmatched event
   * starts a new row at the top; the list is capped so a long session can't grow unbounded.
   */
  applyStreamEvent: (event) => {
    // A replay is in flight — queue rather than land, so it cannot be applied on top of this event.
    // The SSE subscription is still attached and still receiving; only the APPLY is deferred, by the
    // length of one HTTP round trip. Nothing the user did is waiting on this: their own uploads show
    // instantly through `addLocalJourneyRow`, which does not come through here.
    if (liveBufferSeq !== 0) {
      liveBuffer.push(event);
      if (liveBuffer.length >= LIVE_BUFFER_MAX) {
        // Bound hit. Open the gate and let everything through NOW; setting the seq to 0 is also what
        // tells the awaiting `hydrateStream` that its replay is stale and must be discarded.
        const overflowed = liveBuffer;
        liveBuffer = [];
        liveBufferSeq = 0;
        // Synchronous, so no event received later can overtake these.
        for (const queued of overflowed) get().applyStreamEvent(queued);
      }
      return;
    }

    set((state) => {
      const stream = reduceStreamEvent(state.stream, event);
      return { stream };
    });

    // A live 'review' event means the server just raised an exception item. The review queue,
    // per-tab counts, and the sidebar badge are otherwise only fetched on mount, so refetch here
    // (86d3uf18j M3). Debounced to collapse bursts and the hydrate replay of past 'review' events.
    // getReviewQueue is server-scoped to the caller's families, so this leaks nothing.
    if (event.phase === 'review') {
      if (reviewRefetchTimer) clearTimeout(reviewRefetchTimer);
      reviewRefetchTimer = setTimeout(() => void get().fetchReviewQueue(), 400);
    }

    // Same problem, same fix, for the archive (86d3uu104). The registry was fetched once on mount and
    // then only when a filter changed — so a document captured after that never appeared in "All
    // documents", and the only way to see it was to touch a filter or hard-reload. Worse when the
    // user is already sitting on the registry: they watch a scan link in the stream and the list
    // beside it stays wrong. Debounced for the same reasons (bursts + the hydrate replay).
    if (REGISTRY_AFFECTING_PHASES.has(event.phase)) {
      if (registryRefetchTimer) clearTimeout(registryRefetchTimer);
      registryRefetchTimer = setTimeout(() => {
        registryRefetchTimer = null;

        // 86d3z56ma A3 — the gate lives INSIDE the callback, not at scheduling time. Scheduled-time
        // gating would still fire at a hidden tab for any event received in the 399 ms before the
        // user switched away, which is the case this exists for. Reading `get()` in an action is
        // fine; 86d3d1z40 bars store getters in RENDER only.
        //
        // Suppressed is not dropped: the fetch is OWED, and `flushRegistryRefetch` pays it the moment
        // the tab is looked at again, so coming back never shows a stale archive.
        if (!backgroundRegistryFetchAllowed(get().registryVisible) || registryInFlight) {
          registryRefetchOwed = true;
          return;
        }

        // silent: the user didn't ask for this, so the rows must not vanish behind a skeleton.
        void get().fetchDocuments(true);
      }, 400);
    }

    // 86d3yek7d — and the same again for today's numbers, which are now a server aggregate rather
    // than something derived from these rows. Without this they would only move on reload, which is
    // the opposite of what a live page should do; with it, the card follows the feed within one
    // debounce and still survives a reload, an API restart and a different user's permissions.
    //
    // Scheduled, NOT plain-debounced — the ceiling is what keeps the card moving during a catch-up
    // burst instead of freezing until the scanning stops. See scheduleIntakeRefetch.
    if (INTAKE_AFFECTING_PHASES.has(event.phase)) {
      scheduleIntakeRefetch(() => void get().fetchIntakeToday());
    }
  },

  hydrateStream: async () => {
    // Close the gate BEFORE the first await, so it is shut by the time the caller's next statement
    // (`subscribeDocCapture`) can deliver anything. Everything down to the `await` below runs
    // synchronously on the calling tick.
    const seq = ++hydrateSeq;
    liveBuffer = [];
    liveBufferSeq = seq;

    let events: DocStreamEvent[] = [];
    let replayFailed = false;
    try {
      events = await documentCaptureService.getRecentEvents(STREAM_CAP);
    } catch {
      // A failed replay is not worth surfacing — the live stream still fills the panel. Note it and
      // fall through: the queued live events must be released either way, or a 500 on this one
      // request would freeze the feed until the next mount.
      replayFailed = true;
    }

    // A NEWER hydrate started while this one was in flight (StrictMode's double-mount, or a fast
    // remount). It owns the gate and the queue now; this reply is stale in its entirety.
    if (seq !== hydrateSeq) return;

    // The bound was hit while we waited: the queued events have already been applied directly, and
    // this snapshot is older than all of them. Applying it now is precisely the bug. Drop it — and
    // there is nothing left to drain, because the overflow path drained it.
    if (liveBufferSeq !== seq) return;

    // Take the queue and OPEN THE GATE before replaying, so the replay itself is not queued. Safe
    // because everything from here to the drain runs on one synchronous tick: an SSE event arrives as
    // a DOM event (a macrotask), so nothing can overtake the queued events in between.
    const queued = liveBuffer;
    liveBuffer = [];
    liveBufferSeq = 0;

    if (!replayFailed) {
      // Replay oldest-first so morphs land in the order they happened.
      events.forEach((event) => get().applyStreamEvent(event));

      // 86d3yeh5j — the replay's finished rows are this morning's scans, which nobody watched: the
      // clutter the whole ticket is about. Retire them here rather than threading a "this is a
      // replay" flag through the reducers — an unstamped `doneAt` already means exactly "no sweep
      // ever saw this row live", which is true on a cold load and false for the rows the user
      // watched land before navigating away and back.
      //
      // Runs BEFORE the queue is drained, and that ordering is load-bearing: a live event received
      // during hydration describes a scan somebody IS watching, so it must not be caught by a pass
      // whose entire meaning is "nobody saw this finish". Draining afterwards also un-retires any
      // replayed row those events move on (carryRetirement), which is exactly right.
      const replayed = get().stream;
      const stream = retireUnwatched(replayed, Date.now());
      // Reference-equal when there was nothing to retire — skip `set` entirely rather than publish a
      // new state object for a no-op.
      if (stream !== replayed) set({ stream });
    }

    // …and only now the live events, in ARRIVAL order. This is the whole fix: whatever the replay
    // said about a row, the newer events say it last.
    for (const event of queued) get().applyStreamEvent(event);
  },

  sweepStream: () => {
    const current = get().stream;
    const stream = sweepRetired(current, Date.now());
    // The bail that makes a 1.5s interval affordable: no `set` means no re-render of the page that
    // subscribes to `stream` (CLAUDE.md re-render gate).
    if (stream === current) return;
    // Nothing but `stream` moves here. Retiring is a display decision about the FEED, and today's
    // numbers are counted in the database — a retired row cannot touch them (86d3yek7d). That
    // independence is exactly why they stopped being derived from these rows.
    set({ stream });
  },

  setStreamConnection: (streamConnection) => set({ streamConnection }),

  /**
   * A row for a file THIS browser is uploading, shown before the server knows anything —
   * the click must produce a visible row immediately, not after the request resolves.
   */
  addLocalJourneyRow: (key, fileName, clientUploadId) =>
    set((state) => {
      const stream = addLocalRow(state.stream, key, fileName, clientUploadId);
      return { stream };
    }),

  settleReviewedRow: (match) =>
    set((state) => {
      const stream = settleResolvedRow(state.stream, match);
      return { stream };
    }),

  resolveLocalJourneyRow: (key, phase, detail) =>
    set((state) => {
      const stream = resolveLocalRow(state.stream, key, phase, detail);
      return { stream };
    }),

  // ---------------------------------------------------------------- station

  setStationState: (stationState, folderName) =>
    set((state) => ({
      stationState,
      stationFolderName: folderName !== undefined ? folderName : state.stationFolderName,
      // A station that isn't watching isn't holding a queue. Leaving the last numbers on screen
      // would describe work that has stopped — the one thing the queue line must never do.
      stationQueue: stationState === 'watching' ? state.stationQueue : null,
    })),

  setStationQueue: (stationQueue) => set({ stationQueue }),

  // ---------------------------------------------------------------- today (86d3yek7d)

  fetchIntakeToday: async (stationId) => {
    if (stationId) intakeStationId = stationId;
    const seq = ++intakeSeq;

    // Loud ONLY on the first load. Every later call is a background refresh behind an SSE event or a
    // midnight rollover, and swapping numbers already on screen for skeletons would make the card
    // flicker on every scan — the opposite of the calm this page is built around (CLAUDE.md).
    if (get().intakeToday === null) set({ intakeTodayLoading: true, intakeTodayError: null });

    try {
      const intakeToday = await documentCaptureService.getIntakeToday(intakeStationId);
      if (seq !== intakeSeq) return; // a newer request is in flight — its answer wins
      set({ intakeToday, intakeTodayLoading: false, intakeTodayError: null });
    } catch (error) {
      if (seq !== intakeSeq) return;
      set({
        intakeTodayLoading: false,
        intakeTodayError: error instanceof Error ? error.message : 'Could not load today’s numbers.',
      });
    }
  },

  refreshIntakeIfDayChanged: () => {
    const current = get().intakeToday;
    // The server stamped the day it counted, so this needs no clock arithmetic — just a mismatch.
    // Without it a station tab left running through the night keeps showing yesterday's totals under
    // a heading that says "Today", which is the same lie in a different costume.
    if (current === null || current.day === philippineToday()) return;
    void get().fetchIntakeToday();
  },

  // ---------------------------------------------------------------- registry

  setRegistryQuery: (registryQuery) => set({ registryQuery }),

  /**
   * Re-read the archive.
   *
   * `silent` is for refreshes the user did not ask for — a scan completing, or arriving on the tab.
   * A loud refetch raises `registryLoading`, and the panel renders that by REPLACING the table with a
   * skeleton: with a station scanning, the list would blink to a skeleton and back roughly twice per
   * document (captured, then linked), collapsing ~1000px of height each time, yanking the scroll
   * position and pulling rows out from under the pointer mid-click. Rows must stay on screen and only
   * the data swap in — CLAUDE.md's gate: render what's already known, never blank the screen for a
   * background update (86d3uu104).
   *
   * The sequence guard drops a stale response. Before this, every refetch was a deliberate user
   * action; now an autonomous timer competes with the user, so a slow background request could land
   * after a fast filter change and leave the table showing the previous query's rows under the new
   * filter chips, with nothing to indicate it.
   *
   * 86d3z56ma A3 — this call ALWAYS STARTS IMMEDIATELY and supersedes whatever is running, and that is
   * the deliberate asymmetry. All four callers come through here (mount, filter change, tab entry,
   * and the stream's background refresh), so a blanket "never two `/documents` in flight" would queue
   * a filter change behind a twelve-second background query — precisely what the responsiveness gate
   * forbids. Only the BACKGROUND path defers, and it defers before it gets here.
   *
   * Superseding now also ABORTS: the request being replaced is cancelled on the wire and, with the
   * matching `CancellationToken` on `GetDocuments`, stops executing on the host too. Before this a
   * query the browser abandoned at its 30 s timeout kept running on two vCPU.
   */
  fetchDocuments: async (silent = false) => {
    const seq = ++registrySeq;

    // Whatever was owed is about to be satisfied by this fetch — clear it BEFORE awaiting, so an
    // event arriving during the request is the only thing that can leave a new one owed.
    registryRefetchOwed = false;
    registryAbort?.abort();
    const controller = new AbortController();
    registryAbort = controller;
    registryInFlight = true;

    if (!silent) set({ registryLoading: true, registryError: null });
    try {
      const result = await documentCaptureService.getDocuments(get().registryQuery, controller.signal);
      if (seq !== registrySeq) return; // a newer request is in flight — its answer wins
      set({ documents: result.items, registryTotal: result.totalCount, registryLoading: false });
    } catch (error) {
      // An aborted request is a superseded one, so `seq` has already moved and this returns without
      // writing an error — a cancellation must never surface as "Could not load documents."
      if (seq !== registrySeq) return;
      set({
        registryLoading: false,
        registryError: error instanceof Error ? error.message : 'Could not load documents.',
      });
    } finally {
      // Only the CURRENT request owns the flag; a superseded one leaves it to its replacement.
      if (seq === registrySeq) {
        registryInFlight = false;
        registryAbort = null;
        // An event landed mid-flight: exactly one follow-up, and only if the gate still allows it.
        // If it does not, the fetch stays owed for `flushRegistryRefetch`.
        if (registryRefetchOwed && backgroundRegistryFetchAllowed(get().registryVisible)) {
          registryRefetchOwed = false;
          void get().fetchDocuments(true);
        }
      }
    }
  },

  setRegistryVisible: (registryVisible) => set({ registryVisible }),

  /**
   * Pay the owed refetch, if one is owed.
   *
   * Deliberately NOT an unconditional fetch on every foregrounding. The premise of the whole gate is
   * that the SSE stream stays open while the tab is hidden, so the page KNOWS whether anything
   * happened: with nothing owed, the archive on screen is current and a fetch would buy nothing but
   * load — which is the thing being reduced.
   *
   * ⚠️ This is not the "fetch on entering the registry tab" path. That already exists in the page
   * (86d3uu104) and adding a second one here would produce two fetches per entry.
   */
  flushRegistryRefetch: () => {
    if (!registryRefetchOwed) return;
    if (!backgroundRegistryFetchAllowed(get().registryVisible) || registryInFlight) return;
    registryRefetchOwed = false;
    void get().fetchDocuments(true);
  },

  // ---------------------------------------------------------------- review

  /**
   * One fetch of the FULL open queue (server page size capped at 100 — the queue is exception work,
   * not bulk). Everything derives from it: the reason-grouped tab lists, the per-tab counts, the
   * badge total. Keeps the selected item valid across a refetch, or clears it if it was resolved.
   */
  fetchReviewQueue: async () => {
    set({ reviewLoading: true, reviewError: null });
    try {
      const result = await documentCaptureService.getReviewQueue({ pageSize: 100 });
      const { counts } = groupReview(result.items, result.totalCount);
      set((state) => ({
        reviewItems: result.items,
        reviewCounts: counts,
        needsReviewCount: counts.total,
        reviewLoading: false,
        // Drop a selection that no longer exists (it was just resolved).
        selectedReviewItemId: result.items.some((i) => i.id === state.selectedReviewItemId)
          ? state.selectedReviewItemId
          : null,
      }));
    } catch (error) {
      set({
        reviewLoading: false,
        reviewError: error instanceof Error ? error.message : 'Could not load the review queue.',
      });
    }
  },

  openReview: (tab, reviewItemId) =>
    set({
      reviewOverlayOpen: true,
      reviewActiveTab: tab ?? 'scans',
      selectedReviewItemId: reviewItemId ?? null,
    }),

  closeReview: () => set({ reviewOverlayOpen: false, selectedReviewItemId: null }),

  setReviewTab: (reviewActiveTab) => set({ reviewActiveTab, selectedReviewItemId: null }),

  selectReviewItem: (selectedReviewItemId) => set({ selectedReviewItemId }),
}));
